import { Link } from "@remix-run/react";
import React, { useRef, useState } from "react";
import { useCart } from "../../context/CartContext";
import { Image } from "@prisma/client";

interface OptionValues {
  title: string;
}
[];

interface Option {
  name: string;
  optionValues: OptionValues[];
}

interface Variant {
  active: boolean;
  optionValues: string[];
  shopifyId: string;
  id: number;
  title: string;
  price: number;
  images: {
    id: number;
    order: number | null;
    src: string;
    shopifySrc: string | null;
    position: string | null;
    isDefault: boolean;
    isSelectedForPublishing: boolean;
    variantId: number;
  }[];
}

interface Product {
  id: number;
  title: string;
  description: string;
  price: number;
  imageUrl: string;
  handle: string;
  defaultImage: string;
  providerName: string;
  providerId: string;
  shopifyId: string;
  tags: string[];
  store: {
    name: string;
    id: number;
    description: string | null;
    handle: string;
    createdAt: Date;
    updatedAt: Date;
    owner: {
      id: number;
      name: string;
    };
    state: string;
  };
  variants: Variant[];
  options: Option[];
}

interface ProductProps {
  product: Product;
}

const ProductDetail: React.FC<ProductProps> = ({ product }) => {
  const { addToCart } = useCart();

  const [activeImage, setActiveImage] = useState(
    product.variants[0].images[0].src
  );

  const [activeVariant, setActiveVariant] = useState(product.variants[0]);

  const refColor = useRef<HTMLSelectElement>(null);
  const refSize = useRef<HTMLSelectElement>(null);

  function handleChangeVariant() {
    const color = refColor.current?.value;
    const size = refSize.current?.value;

    const variant = product.variants.find((v: Variant) => {
      const variantColor =
        v.optionValues[
          product.options.findIndex((o: { name: string }) => o.name === "Color")
        ];
      const variantSize =
        v.optionValues[
          product.options.findIndex((o: { name: string }) => o.name === "Size")
        ];
      return (
        (variantColor === color && variantSize === size) ||
        (variantColor === size && variantSize === color)
      );
    });
    setActiveVariant(variant!);
    setActiveImage(variant!.images[0].src);
  }

  function handleAtc(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();
    addToCart(activeVariant!.shopifyId, 1);
  }

  return (
    <div className="product-detail">
      <section className="container mx-auto p-4 md:p-6 lg:px-10 lg:max-w-7xl">
        <article>
          {/* Breadcrumb Navigation */}
          <nav className="md:mb-6">
            <div className="flex items-center text-sm space-x-1 overflow-x-auto">
              <Link
                to={`/${product.store.handle}`}
                className="text-black hover:underline whitespace-nowrap"
              >
                {product.store.name}
              </Link>
              <span>/</span>
              <Link
                to={`/${product.store.handle}/p/${product.handle}`}
                className="text-black hover:underline whitespace-nowrap"
              >
                {product.title}
              </Link>
            </div>
          </nav>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-10">
            {/* Gallery Section */}
            <section>
              <img
                src={activeImage}
                className="w-full aspect-square object-cover"
                alt={product.title}
              />
              <div className="w-full overflow-x-auto">
                <div className="w-max inline-flex gap-2 mx-auto">
                  {activeVariant.images.map((image, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        if (image.shopifySrc) {
                          setActiveImage(image.shopifySrc);
                        }
                      }}
                      className="w-14 h-14 object-cover border border-gray-300 rounded-lg p-1"
                      aria-label={`Select image of ${product.title}`}
                    >
                      <img
                        src={`${image.shopifySrc!}&width=100`}
                        alt={product.title}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            </section>

            <div>
              {/* Product Details */}
              <section>
                <h1 className="text-4xl font-medium mb-4">{product.title}</h1>

                <div className="text-2xl text-gray-700 mb-4">
                  ${product.variants[0].price}
                </div>

                {/* ATC */}
                <div className="fixed bottom-0 left-0 w-full px-4 py-2 bg-white shadow-[0px_-2px_4px_rgba(50,50,50,0.25)] md:static md:bg-transparent md:shadow-none md:mb-4 md:px-0">
                  <div className="flex w-full gap-2 mb-2">
                    {product.options.map((option: Option, index: number) => {
                      // Sort size options if this is a size option
                      const optionValues = [...option.optionValues];
                      if (option.name === "Size") {
                        const sizeOrder = [
                          "S",
                          "M",
                          "L",
                          "XL",
                          "2XL",
                          "3XL",
                          "4XL",
                        ];
                        optionValues.sort((a, b) => {
                          const aIndex = sizeOrder.indexOf(a.title);
                          const bIndex = sizeOrder.indexOf(b.title);
                          return aIndex - bIndex;
                        });
                      }

                      // RELIABLY GET THE DEFAULT VALUE
                      const defaultValue = product.options[index].optionValues.find(
                        (ov) => ov.title == activeVariant.optionValues[0] || ov.title == activeVariant.optionValues[1]
                      );

                      return (
                        <div
                          key={index}
                          className={`flex flex-col w-full ${option.name === "Color" ? "order-1" : "order-2"}`}
                        >
                          <label className="text-xs font-medium mb-1">
                            {option.name}
                          </label>
                          <select
                            className={`border border-gray-300 rounded-lg p-2 md:p-3`}
                            ref={option.name === "Color" ? refColor : refSize}
                            onChange={handleChangeVariant}
                            value={defaultValue!.title}
                          >
                            {optionValues
                              .filter((value: { title: string }) =>
                                product.variants.some(
                                  (variant: Variant) =>
                                    variant.optionValues[0] === value.title ||
                                    variant.optionValues[1] === value.title
                                )
                              )
                              .map((value: { title: string }, i: number) => (
                                <option key={i} value={value.title}>
                                  {value.title}
                                </option>
                              ))}
                          </select>
                        </div>
                      );
                    })}
                  </div>

                  <button
                    onClick={handleAtc}
                    className="bg-black text-white py-2 px-3 w-full rounded-lg hover:bg-gray-800 uppercase md:px-5 md:py-3"
                  >
                    Add To Cart
                  </button>
                </div>

                <div
                  className="text-gray-700 text-lg leading-relaxed mb-6"
                  dangerouslySetInnerHTML={{
                    __html: product.description.replace(
                      /(?:\r\n|\r|\n)/g,
                      `<br/>`
                    ),
                  }}
                ></div>
              </section>
            </div>
          </div>
        </article>
      </section>
    </div>
  );
};

export default ProductDetail;
